import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { AlertTriangle, ArrowLeft, Boxes, Layers, Pencil, Tags, XCircle } from 'lucide-react';
import { adminService } from '../../../../services';
import { ApiError } from '../../../../lib/apiClient';
import './Admin.css';

const defaultCategoryForm = {
    name: '',
    type: 'Both',
    description: '',
    isActive: true,
    showModel: true,
    showSerialNumber: false,
    isPrimarySelectable: true,
    pricingHelpText: ''
};

const defaultBrandForm = {
    name: '',
    categoryId: '',
    isActive: true
};

const defaultSpecificationForm = {
    categoryId: '',
    name: '',
    type: 'Text',
    options: '',
    unit: '',
    isRequired: false
};

const AdminEquipmentData = () => {
    const { user } = useUser();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [activeTab, setActiveTab] = useState('categories');

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [specifications, setSpecifications] = useState([]);

    const [categoryForm, setCategoryForm] = useState(defaultCategoryForm);
    const [brandForm, setBrandForm] = useState(defaultBrandForm);
    const [specificationForm, setSpecificationForm] = useState(defaultSpecificationForm);

    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editingBrandId, setEditingBrandId] = useState(null);
    const [editingSpecificationId, setEditingSpecificationId] = useState(null);

    const resetFlash = useCallback((message) => {
        setSuccessMessage(message || '');
        setError(null);
    }, []);

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            const [categoriesData, brandsData, specsData] = await Promise.all([
                adminService.getEquipmentCategories(),
                adminService.getEquipmentBrands(),
                adminService.getEquipmentSpecifications()
            ]);

            setCategories(Array.isArray(categoriesData) ? categoriesData : []);
            setBrands(Array.isArray(brandsData) ? brandsData : []);
            setSpecifications(Array.isArray(specsData) ? specsData : []);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setError('You do not have admin access.');
            } else {
                setError(err.message || 'Failed to load equipment admin data.');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
        [categories]
    );

    const beginEditCategory = (category) => {
        setEditingCategoryId(category.categoryId);
        setCategoryForm({
            name: category.name || '',
            type: category.type || 'Both',
            description: category.description || '',
            isActive: Boolean(category.isActive),
            showModel: Boolean(category.showModel),
            showSerialNumber: Boolean(category.showSerialNumber),
            isPrimarySelectable: Boolean(category.isPrimarySelectable),
            pricingHelpText: category.pricingHelpText || ''
        });
        setActiveTab('categories');
        resetFlash('');
    };

    const beginEditBrand = (brand) => {
        setEditingBrandId(brand.brandId);
        setBrandForm({
            name: brand.name || '',
            categoryId: String(brand.categoryId || ''),
            isActive: Boolean(brand.isActive)
        });
        setActiveTab('brands');
        resetFlash('');
    };

    const beginEditSpecification = (spec) => {
        setEditingSpecificationId(spec.definitionId);
        setSpecificationForm({
            categoryId: String(spec.categoryId || ''),
            name: spec.name || '',
            type: spec.type || 'Text',
            options: spec.options || '',
            unit: spec.unit || '',
            isRequired: Boolean(spec.isRequired)
        });
        setActiveTab('specifications');
        resetFlash('');
    };

    const clearCategoryForm = () => {
        setEditingCategoryId(null);
        setCategoryForm(defaultCategoryForm);
    };

    const clearBrandForm = () => {
        setEditingBrandId(null);
        setBrandForm(defaultBrandForm);
    };

    const clearSpecificationForm = () => {
        setEditingSpecificationId(null);
        setSpecificationForm(defaultSpecificationForm);
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        if (!categoryForm.name.trim()) {
            setError('Category name is required.');
            return;
        }

        try {
            setError(null);
            const payload = {
                ...categoryForm,
                name: categoryForm.name.trim(),
                type: categoryForm.type.trim(),
                description: categoryForm.description.trim(),
                pricingHelpText: categoryForm.pricingHelpText.trim()
            };

            if (editingCategoryId) {
                await adminService.updateEquipmentCategory(editingCategoryId, payload);
                resetFlash('Category updated.');
            } else {
                await adminService.createEquipmentCategory(payload);
                resetFlash('Category created.');
            }

            clearCategoryForm();
            await loadData();
        } catch (err) {
            setError(err.message || 'Failed to save category.');
        }
    };

    const handleBrandSubmit = async (e) => {
        e.preventDefault();
        if (!brandForm.name.trim() || !brandForm.categoryId) {
            setError('Brand name and category are required.');
            return;
        }

        try {
            setError(null);
            const payload = {
                name: brandForm.name.trim(),
                categoryId: Number(brandForm.categoryId),
                isActive: brandForm.isActive
            };

            if (editingBrandId) {
                await adminService.updateEquipmentBrand(editingBrandId, payload);
                resetFlash('Brand updated.');
            } else {
                await adminService.createEquipmentBrand(payload);
                resetFlash('Brand created.');
            }

            clearBrandForm();
            await loadData();
        } catch (err) {
            setError(err.message || 'Failed to save brand.');
        }
    };

    const handleSpecificationSubmit = async (e) => {
        e.preventDefault();
        if (!specificationForm.categoryId || !specificationForm.name.trim()) {
            setError('Specification category and name are required.');
            return;
        }

        if (specificationForm.type === 'Select' && !specificationForm.options.trim()) {
            setError('Select specifications require JSON options.');
            return;
        }

        try {
            setError(null);
            const payload = {
                categoryId: Number(specificationForm.categoryId),
                name: specificationForm.name.trim(),
                type: specificationForm.type,
                options: specificationForm.options.trim() || null,
                unit: specificationForm.unit.trim() || null,
                isRequired: specificationForm.isRequired
            };

            if (editingSpecificationId) {
                await adminService.updateEquipmentSpecification(editingSpecificationId, payload);
                resetFlash('Specification updated.');
            } else {
                await adminService.createEquipmentSpecification(payload);
                resetFlash('Specification created.');
            }

            clearSpecificationForm();
            await loadData();
        } catch (err) {
            setError(err.message || 'Failed to save specification.');
        }
    };

    if (!user?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access equipment data admin.</p>
                    <Link to="/dashboard/home" className="admin-btn admin-btn-primary">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="admin-page">
                <div className="admin-loading">
                    <div className="admin-spinner" />
                    <p>Loading equipment data admin...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <Link to="/dashboard/admin" className="admin-back-btn">
                        <ArrowLeft size={18} />
                    </Link>
                    <div className="admin-header-icon">
                        <Boxes size={28} />
                    </div>
                    <div>
                        <h1>Equipment Data Admin</h1>
                        <p>Manage categories, brands, and specifications used by equipment forms.</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)}>
                        <XCircle size={16} />
                    </button>
                </div>
            )}

            {successMessage && (
                <div className="admin-alert">
                    <span>{successMessage}</span>
                    <button type="button" onClick={() => setSuccessMessage('')}>
                        <XCircle size={16} />
                    </button>
                </div>
            )}

            <div className="admin-equipment-tabs">
                <button className={`admin-btn ${activeTab === 'categories' ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => setActiveTab('categories')}>
                    <Layers size={16} />
                    Categories
                </button>
                <button className={`admin-btn ${activeTab === 'brands' ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => setActiveTab('brands')}>
                    <Tags size={16} />
                    Brands
                </button>
                <button className={`admin-btn ${activeTab === 'specifications' ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => setActiveTab('specifications')}>
                    <Pencil size={16} />
                    Specifications
                </button>
            </div>

            {activeTab === 'categories' && (
                <div className="admin-equipment-grid">
                    <div className="admin-equipment-panel">
                        <h3>{editingCategoryId ? 'Edit Category' : 'Add Category'}</h3>
                        <form onSubmit={handleCategorySubmit} className="admin-modal-form">
                            <div className="admin-form-group">
                                <label>Name</label>
                                <input value={categoryForm.name} onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))} />
                            </div>
                            <div className="admin-form-group">
                                <label>Type</label>
                                <select value={categoryForm.type} onChange={(e) => setCategoryForm(prev => ({ ...prev, type: e.target.value }))}>
                                    <option value="Both">Both</option>
                                    <option value="Clay">Clay</option>
                                    <option value="Rifle">Rifle</option>
                                    <option value="Game">Game</option>
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Description</label>
                                <textarea value={categoryForm.description} onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))} rows={3} />
                            </div>
                            <div className="admin-form-group">
                                <label>Pricing Help Text</label>
                                <textarea value={categoryForm.pricingHelpText} onChange={(e) => setCategoryForm(prev => ({ ...prev, pricingHelpText: e.target.value }))} rows={2} />
                            </div>
                            <div className="admin-form-checkboxes">
                                <label className="admin-checkbox"><input type="checkbox" checked={categoryForm.isActive} onChange={(e) => setCategoryForm(prev => ({ ...prev, isActive: e.target.checked }))} /> Active</label>
                                <label className="admin-checkbox"><input type="checkbox" checked={categoryForm.showModel} onChange={(e) => setCategoryForm(prev => ({ ...prev, showModel: e.target.checked }))} /> Show model</label>
                                <label className="admin-checkbox"><input type="checkbox" checked={categoryForm.showSerialNumber} onChange={(e) => setCategoryForm(prev => ({ ...prev, showSerialNumber: e.target.checked }))} /> Show serial</label>
                                <label className="admin-checkbox"><input type="checkbox" checked={categoryForm.isPrimarySelectable} onChange={(e) => setCategoryForm(prev => ({ ...prev, isPrimarySelectable: e.target.checked }))} /> Primary selectable</label>
                            </div>
                            <div className="admin-modal-actions">
                                {editingCategoryId && <button type="button" className="admin-btn admin-btn-secondary" onClick={clearCategoryForm}>Cancel Edit</button>}
                                <button type="submit" className="admin-btn admin-btn-primary">{editingCategoryId ? 'Save Category' : 'Add Category'}</button>
                            </div>
                        </form>
                    </div>
                    <div className="admin-equipment-panel">
                        <h3>Existing Categories</h3>
                        <div className="admin-equipment-table-wrap">
                            <table className="admin-users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Brands</th>
                                        <th>Specs</th>
                                        <th>Status</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedCategories.map((category) => (
                                        <tr key={category.categoryId}>
                                            <td>{category.name}</td>
                                            <td>{category.type}</td>
                                            <td>{category.brandCount}</td>
                                            <td>{category.specificationCount}</td>
                                            <td>{category.isActive ? 'Active' : 'Inactive'}</td>
                                            <td>
                                                <button className="admin-btn admin-btn-icon" onClick={() => beginEditCategory(category)}>
                                                    <Pencil size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'brands' && (
                <div className="admin-equipment-grid">
                    <div className="admin-equipment-panel">
                        <h3>{editingBrandId ? 'Edit Brand' : 'Add Brand'}</h3>
                        <form onSubmit={handleBrandSubmit} className="admin-modal-form">
                            <div className="admin-form-group">
                                <label>Name</label>
                                <input value={brandForm.name} onChange={(e) => setBrandForm(prev => ({ ...prev, name: e.target.value }))} />
                            </div>
                            <div className="admin-form-group">
                                <label>Category</label>
                                <select value={brandForm.categoryId} onChange={(e) => setBrandForm(prev => ({ ...prev, categoryId: e.target.value }))}>
                                    <option value="">Select category</option>
                                    {sortedCategories.map((category) => (
                                        <option key={category.categoryId} value={category.categoryId}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-checkboxes">
                                <label className="admin-checkbox"><input type="checkbox" checked={brandForm.isActive} onChange={(e) => setBrandForm(prev => ({ ...prev, isActive: e.target.checked }))} /> Active</label>
                            </div>
                            <div className="admin-modal-actions">
                                {editingBrandId && <button type="button" className="admin-btn admin-btn-secondary" onClick={clearBrandForm}>Cancel Edit</button>}
                                <button type="submit" className="admin-btn admin-btn-primary">{editingBrandId ? 'Save Brand' : 'Add Brand'}</button>
                            </div>
                        </form>
                    </div>
                    <div className="admin-equipment-panel">
                        <h3>Existing Brands</h3>
                        <div className="admin-equipment-table-wrap">
                            <table className="admin-users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Status</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {brands.map((brand) => (
                                        <tr key={brand.brandId}>
                                            <td>{brand.name}</td>
                                            <td>{brand.categoryName}</td>
                                            <td>{brand.isActive ? 'Active' : 'Inactive'}</td>
                                            <td>
                                                <button className="admin-btn admin-btn-icon" onClick={() => beginEditBrand(brand)}>
                                                    <Pencil size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'specifications' && (
                <div className="admin-equipment-grid">
                    <div className="admin-equipment-panel">
                        <h3>{editingSpecificationId ? 'Edit Specification' : 'Add Specification'}</h3>
                        <form onSubmit={handleSpecificationSubmit} className="admin-modal-form">
                            <div className="admin-form-group">
                                <label>Category</label>
                                <select value={specificationForm.categoryId} onChange={(e) => setSpecificationForm(prev => ({ ...prev, categoryId: e.target.value }))}>
                                    <option value="">Select category</option>
                                    {sortedCategories.map((category) => (
                                        <option key={category.categoryId} value={category.categoryId}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Name</label>
                                <input value={specificationForm.name} onChange={(e) => setSpecificationForm(prev => ({ ...prev, name: e.target.value }))} />
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Type</label>
                                    <select value={specificationForm.type} onChange={(e) => setSpecificationForm(prev => ({ ...prev, type: e.target.value }))}>
                                        <option value="Text">Text</option>
                                        <option value="Number">Number</option>
                                        <option value="Boolean">Boolean</option>
                                        <option value="Select">Select</option>
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Unit</label>
                                    <input value={specificationForm.unit} onChange={(e) => setSpecificationForm(prev => ({ ...prev, unit: e.target.value }))} placeholder="mm, inches, etc." />
                                </div>
                            </div>
                            <div className="admin-form-group">
                                <label>Options (JSON array for Select type)</label>
                                <textarea value={specificationForm.options} onChange={(e) => setSpecificationForm(prev => ({ ...prev, options: e.target.value }))} rows={3} placeholder='["Option 1","Option 2"]' />
                            </div>
                            <div className="admin-form-checkboxes">
                                <label className="admin-checkbox"><input type="checkbox" checked={specificationForm.isRequired} onChange={(e) => setSpecificationForm(prev => ({ ...prev, isRequired: e.target.checked }))} /> Required</label>
                            </div>
                            <div className="admin-modal-actions">
                                {editingSpecificationId && <button type="button" className="admin-btn admin-btn-secondary" onClick={clearSpecificationForm}>Cancel Edit</button>}
                                <button type="submit" className="admin-btn admin-btn-primary">{editingSpecificationId ? 'Save Specification' : 'Add Specification'}</button>
                            </div>
                        </form>
                    </div>
                    <div className="admin-equipment-panel">
                        <h3>Existing Specifications</h3>
                        <div className="admin-equipment-table-wrap">
                            <table className="admin-users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Type</th>
                                        <th>Required</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {specifications.map((spec) => (
                                        <tr key={spec.definitionId}>
                                            <td>{spec.name}</td>
                                            <td>{spec.categoryName}</td>
                                            <td>{spec.type}</td>
                                            <td>{spec.isRequired ? 'Yes' : 'No'}</td>
                                            <td>
                                                <button className="admin-btn admin-btn-icon" onClick={() => beginEditSpecification(spec)}>
                                                    <Pencil size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEquipmentData;
